/* eslint-disable @typescript-eslint/init-declarations -- for now*/
/* eslint-disable no-magic-numbers -- for now*/
/* eslint-disable no-unreachable-loop -- for now*/
// @/services/helia/core/cidUtils.ts
import { CID } from 'multiformats/cid';
import { sha256 } from 'multiformats/hashes/sha2';
import * as raw from 'multiformats/codecs/raw';

import type { HeliaNode } from '@/types/poc/helia';
import { createLogger, type Logger } from '@/utils/logger';

const log: Logger = createLogger({ namespace: 'six3ro:utils:cid' });

/**
 * Convert a text CID string to a CID object
 *
 * @param cidString - The string representation of a CID
 * @returns Parsed CID object
 * @throws Error if the CID string is invalid
 */
export const parseCid = (cidString: string): CID => {
  try {
    return CID.parse(cidString);
  } catch (error) {
    throw new Error(`Invalid CID: ${cidString}`);
  }
};

/**
 * Check if a string is a valid CID
 *
 * @param cidString - The string to check
 * @returns True if the string is a valid CID, false otherwise
 */
export const isValidCid = (cidString: string): boolean => {
  try {
    CID.parse(cidString);
    return true;
  } catch {
    return false;
  }
};

/**
 * Normalize a CID to ensure consistent format
 *
 * @param cid - The CID string or object to normalize
 * @returns Normalized CID string
 */
export const normalizeCid = (cid: string | CID): string => {
  const parsedCid = typeof cid === 'string' ? parseCid(cid) : cid;
  return parsedCid.toString();
};

/**
 * Generate a shortened display version of a CID
 *
 * @param cid - The CID string to shorten
 * @param length - The number of characters to include (default: 8)
 * @returns Shortened CID string
 */
export const shortenCid = (cid: string, length = 8): string => cid.substring(0, length);

/**
 * Generate a filename from a CID with optional prefix
 *
 * @param cid - The CID string
 * @param prefix - Optional prefix for the filename (default: 'file')
 * @returns A filename based on the CID
 */
export const filenameFromCid = (cid: string, prefix = 'file'): string => `${prefix}-${shortenCid(cid)}`;

/**
 * Creates a Content Identifier (CID) for the given data
 * @param data The data to create a CID for (Blob or Uint8Array)
 * @returns A CID string
 */
export async function createCID(data: Blob | Uint8Array): Promise<string> {
  // Convert Blob to Uint8Array if needed
  let bytes: Uint8Array;
  if (data instanceof Blob) {
    const buffer = await data.arrayBuffer();
    bytes = new Uint8Array(buffer);
  } else {
    bytes = data;
  }

  // Hash the data
  const hash = await sha256.digest(bytes);

  // Create a CID using the raw codec and SHA-256 hash
  const cid = CID.create(1, raw.code, hash);

  // Return the string representation
  return cid.toString();
}

/**
 * Validates that a given CID exists in Helia
 * @param cidString The CID string to validate
 * @param heliaNode The Helia node instance
 * @returns Promise resolving to true if the CID exists, false otherwise
 */
export async function validateCIDExistsInHelia(
  cidString: string,
  heliaNode: HeliaNode
): Promise<boolean> {
  try {
    if (!isValidCid(cidString)) {
      return false;
    }

    const cid = parseCid(cidString);
    const { fs } = heliaNode;

    // Try to get the first chunk of data
    // If this doesn't throw, the CID exists
    for await (const chunk of fs.cat(cid, { offset: 0, length: 1 })) {
      return true;
    }

    return false;
  } catch (error) {
    log.error('Error validating CID:', error);
    return false;
  }
}

/**
 * Extracts the raw data from a CID in Helia
 * @param cidString The CID string to get data for
 * @param heliaNode The Helia node instance
 * @returns Promise resolving to the Uint8Array data
 */
export async function getCIDData(
  cidString: string,
  heliaNode: HeliaNode
): Promise<Uint8Array> {
  try {
    const cid = parseCid(cidString);
    const { fs } = heliaNode;

    // Get all data chunks
    const chunks: Uint8Array[] = [];
    for await (const chunk of fs.cat(cid)) {
      chunks.push(chunk);
    }

    // Combine chunks
    const totalLength = chunks.reduce((len, chunk) => len + chunk.length, 0);
    const result = new Uint8Array(totalLength);
    let offset = 0;

    for (const chunk of chunks) {
      result.set(chunk, offset);
      offset += chunk.length;
    }

    return result;
  } catch (error) {
    log.error('Error getting CID data:', error);
    throw error;
  }
}
